const {
    InMemoryCache,
    defaultDataIdFromObject
} = require('apollo-cache-inmemory');
const {isEqual} = require('lodash');

const OPERATION_DEFINITION = 'OperationDefinition';

const getFilename = (src) => (src ? src.split('/').pop() : '');

const digest = (object) =>
    `${object.__typename}:${Object.values(object)
        .filter((v) => v !== object.__typename)
        .join('|')}`;

const dataIdFromObject = (object) => {
    const typename = object.__typename;
    switch (typename) {
        /*
        When adding new IDs you must ensure that the format is such that
        it will be globally unique _across the entire schema_ used by SERP

        In addition the object you are making an ID for must not vary based
        on the particular search (e.g. filter counts).
        */

        case 'Badge':
            return `${typename}:${object.name}`;
        case 'HitImage':
            return `${typename}:${getFilename(object.src || object.lowResSrc)}`;
        case 'Coordinates':
        case 'HitSuperlative':
        case 'Details':
            return digest(object);
        default:
            return defaultDataIdFromObject(object);
    }
};

const getQueryName = (query) => {
    const defs = query.definitions;
    /* istanbul ignore else */
    if (defs && defs.length) {
        const operationDefinition = defs.filter(
            ({kind}) => kind === OPERATION_DEFINITION
        );
        return (
            operationDefinition.length &&
            operationDefinition[0].name &&
            operationDefinition[0].name.value
        );
    }
    /* istanbul ignore next */
    return null;
};

const SEARCH_QUERY_NAME = 'SearchQuery';
const FILTER_COUNT_QUERY_NAME = 'FilterCountQuery';

/**
 * Optimized version of InMemoryCache which caches the first SearchQuery query for the initial
 * pageload to avoid the expensive normalization process for the server side page load for the
 * very large SearchQuery GraphQL query.
 */
class OptimizedInMemoryCache extends InMemoryCache {
    extract(optimistic) {
        const normalizedCache = super.extract(optimistic);
        return {_INITIAL_QUERY: this._INITIAL_QUERY, ...normalizedCache};
    }

    restore(data) {
        this._INITIAL_QUERY = data._INITIAL_QUERY;
        return super.restore(data);
    }

    reset() {
        this._INITIAL_QUERY = null;
        return super.reset();
    }

    write(write) {
        if (
            SEARCH_QUERY_NAME === getQueryName(write.query) &&
            !this._INITIAL_QUERY
        ) {
            // Save the first SearchQuery query, don't normalize this to the cache
            this._INITIAL_QUERY = {
                result: write.result,
                variables: write.variables
            };
            super.broadcastWatches();
            return;
        }
        super.write(write);
    }

    read(query) {
        if (this.useInitialQuery(query)) {
            return this._INITIAL_QUERY.result;
        }
        return super.read(query);
    }

    diff(query) {
        if (this.useInitialQuery(query)) {
            return {result: this._INITIAL_QUERY.result, complete: true};
        }
        return super.diff(query);
    }

    useInitialQuery(query) {
        return (
            [FILTER_COUNT_QUERY_NAME, SEARCH_QUERY_NAME].includes(
                getQueryName(query.query)
            ) &&
            this._INITIAL_QUERY &&
            isEqual(this._INITIAL_QUERY.variables, query.variables)
        );
    }
}

const createCache = () =>
    new OptimizedInMemoryCache({
        dataIdFromObject
    });

module.exports = {createCache, dataIdFromObject};
